/* eslint-disable react/prop-types */
import {
	useState,
	useEffect,
	useRef,
	useCallback,
	forwardRef,
	useImperativeHandle
} from "react";
import { UserCircle, Hash } from "@phosphor-icons/react";
import cmsClient from "src/services/cmsClient";

interface Suggestion {
	id: string;
	name: string;
	subtitle?: string;
}

interface MentionHashtagInputProps {
	value: string;
	onChange: (value: string) => void;
	placeholder?: string;
	className?: string;
	autoFocus?: boolean;
	onKeyDown?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
	minHeight?: string;
	disabled?: boolean;
}

const MentionHashtagInput = forwardRef<
	HTMLTextAreaElement,
	MentionHashtagInputProps
>(
	(
		{
			value,
			onChange,
			placeholder,
			className,
			autoFocus,
			onKeyDown,
			minHeight = "120px",
			disabled = false
		},
		ref
	) => {
		const textareaRef = useRef<HTMLTextAreaElement>(null);
		const containerRef = useRef<HTMLDivElement>(null);
		const dropdownRef = useRef<HTMLDivElement>(null);

		useImperativeHandle(ref, () => textareaRef.current as HTMLTextAreaElement);

		const [trigger, setTrigger] = useState<"@" | "#" | null>(null);
		const [query, setQuery] = useState("");
		const [debouncedQuery, setDebouncedQuery] = useState("");
		const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
		const [triggerStart, setTriggerStart] = useState(0);
		const [cursorEnd, setCursorEnd] = useState(0);
		const [selectedIndex, setSelectedIndex] = useState(0);
		const [isLoading, setIsLoading] = useState(false);

		// Debounce the query
		useEffect(() => {
			const timer = setTimeout(() => {
				setDebouncedQuery(query);
			}, 300);
			return () => clearTimeout(timer);
		}, [query]);

		// Fetch suggestions
		useEffect(() => {
			if (!debouncedQuery || debouncedQuery.length < 1 || !trigger) {
				setSuggestions([]);
				setIsLoading(false);
				return;
			}

			let cancelled = false;
			setIsLoading(true);

			if (trigger === "@") {
				cmsClient
					.get("/socialprofile/search/", {
						params: { search_term: debouncedQuery }
					})
					.then((res) => {
						if (cancelled) return;
						setSuggestions(
							(res.data || []).map(
								(u: {
									username: string;
									full_name?: string;
									role?: string;
								}) => ({
									id: u.username,
									name: u.full_name || u.username,
									subtitle: u.role ?? ""
								})
							)
						);
						setIsLoading(false);
					})
					.catch(() => {
						if (!cancelled) {
							setSuggestions([]);
							setIsLoading(false);
						}
					});
			}

			if (trigger === "#") {
				cmsClient
					.get("/hashtag/", {
						params: { search: debouncedQuery }
					})
					.then((res) => {
						if (cancelled) return;
						setSuggestions(
							(res.data?.results || []).map(
								(h: { uuid?: string; name: string; description?: string }) => ({
									id: h.uuid || h.name,
									name: h.name,
									subtitle: h.description ?? ""
								})
							)
						);
						setIsLoading(false);
					})
					.catch(() => {
						if (!cancelled) {
							setSuggestions([]);
							setIsLoading(false);
						}
					});
			}

			return () => {
				cancelled = true;
			};
		}, [debouncedQuery, trigger]);

		// Reset selected index when suggestions change
		useEffect(() => {
			setSelectedIndex(0);
		}, [suggestions]);

		// Close dropdown on click outside
		useEffect(() => {
			const handleClickOutside = (e: MouseEvent) => {
				if (
					containerRef.current &&
					!containerRef.current.contains(e.target as Node)
				) {
					setTrigger(null);
					setQuery("");
					setSuggestions([]);
				}
			};

			document.addEventListener("mousedown", handleClickOutside);
			return () =>
				document.removeEventListener("mousedown", handleClickOutside);
		}, []);

		const detectTrigger = useCallback((text: string, cursorPos: number) => {
			// Look backwards from cursor for @ or #
			const textBeforeCursor = text.substring(0, cursorPos);

			// Find the last @ or # that isn't preceded by a word character
			let lastTriggerPos = -1;
			let lastTriggerChar: "@" | "#" | null = null;

			for (let i = textBeforeCursor.length - 1; i >= 0; i--) {
				const char = textBeforeCursor[i];

				// If we hit a space or newline before finding a trigger, stop
				if (char === " " || char === "\n" || char === "\r") {
					break;
				}

				if (char === "@" || char === "#") {
					// Check the character before the trigger
					const charBefore = i > 0 ? textBeforeCursor[i - 1] : undefined;
					// Valid if at start of text, after a space, or after a newline
					if (
						!charBefore ||
						charBefore === " " ||
						charBefore === "\n" ||
						charBefore === "\r"
					) {
						lastTriggerPos = i;
						lastTriggerChar = char as "@" | "#";
					}
					break;
				}
			}

			if (lastTriggerChar && lastTriggerPos >= 0) {
				const queryText = textBeforeCursor.substring(lastTriggerPos + 1);
				if (queryText.length >= 1) {
					setTrigger(lastTriggerChar);
					setQuery(queryText);
					setTriggerStart(lastTriggerPos);
					setCursorEnd(cursorPos);
					return;
				} else if (queryText.length === 0) {
					// Just typed the trigger char, wait for more input
					setTrigger(lastTriggerChar);
					setQuery("");
					setTriggerStart(lastTriggerPos);
					setCursorEnd(cursorPos);
					setSuggestions([]);
					return;
				}
			}

			// No active trigger
			setTrigger(null);
			setQuery("");
			setSuggestions([]);
		}, []);

		const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
			const newValue = e.target.value;
			onChange(newValue);
			const cursorPos = e.target.selectionStart ?? 0;
			detectTrigger(newValue, cursorPos);
		};

		const handleSelect = useCallback(
			(suggestion: Suggestion) => {
				const before = value.substring(0, triggerStart);
				const after = value.substring(cursorEnd);

				let insertText = "";
				if (trigger === "@") {
					insertText = `@[${suggestion.name}](${suggestion.id}) `;
				} else if (trigger === "#") {
					insertText = `#[${suggestion.name}](${suggestion.id}) `;
				}

				const newValue = before + insertText + after;
				onChange(newValue);

				setTrigger(null);
				setQuery("");
				setSuggestions([]);

				// Restore focus and set cursor position after the inserted text
				requestAnimationFrame(() => {
					if (textareaRef.current) {
						textareaRef.current.focus();
						const newCursorPos = before.length + insertText.length;
						textareaRef.current.setSelectionRange(newCursorPos, newCursorPos);
					}
				});
			},
			[value, triggerStart, cursorEnd, trigger, onChange]
		);

		const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
			if (trigger && suggestions.length > 0) {
				if (e.key === "ArrowDown") {
					e.preventDefault();
					setSelectedIndex((prev) =>
						prev < suggestions.length - 1 ? prev + 1 : 0
					);
					return;
				}
				if (e.key === "ArrowUp") {
					e.preventDefault();
					setSelectedIndex((prev) =>
						prev > 0 ? prev - 1 : suggestions.length - 1
					);
					return;
				}
				if (e.key === "Enter") {
					e.preventDefault();
					handleSelect(suggestions[selectedIndex]);
					return;
				}
				if (e.key === "Tab") {
					e.preventDefault();
					handleSelect(suggestions[selectedIndex]);
					return;
				}
			}

			if (e.key === "Escape" && trigger) {
				e.preventDefault();
				setTrigger(null);
				setQuery("");
				setSuggestions([]);
				return;
			}

			// Pass through to parent handler
			onKeyDown?.(e);
		};

		// Scroll selected item into view
		useEffect(() => {
			if (dropdownRef.current && suggestions.length > 0) {
				const selected = dropdownRef.current.children[
					selectedIndex
				] as HTMLElement;
				if (selected) {
					selected.scrollIntoView({ block: "nearest" });
				}
			}
		}, [selectedIndex, suggestions.length]);

		const showDropdown = trigger !== null && suggestions.length > 0;

		return (
			<div ref={containerRef} className="relative">
				<textarea
					ref={textareaRef}
					value={value}
					onChange={handleChange}
					onKeyDown={handleKeyDown}
					onClick={() => {
						if (textareaRef.current) {
							const cursorPos = textareaRef.current.selectionStart ?? 0;
							detectTrigger(value, cursorPos);
						}
					}}
					placeholder={placeholder}
					className={className}
					style={{ minHeight }}
					autoFocus={autoFocus}
					disabled={disabled}
				/>

				{showDropdown && (
					<div
						ref={dropdownRef}
						className="absolute left-0 right-0 z-10 mt-1 max-h-[200px] overflow-y-auto rounded-lg border border-border bg-white shadow-lg"
						role="listbox"
						aria-label={
							trigger === "@" ? "Mention suggestions" : "Hashtag suggestions"
						}
					>
						{suggestions.map((suggestion, index) => (
							<button
								key={suggestion.id}
								type="button"
								role="option"
								aria-selected={index === selectedIndex}
								aria-label={`Select ${trigger}${suggestion.name}`}
								className={[
									"flex w-full items-center gap-3 px-3 py-2 text-left text-sm transition-colors",
									index === selectedIndex ? "bg-muted" : "hover:bg-muted/50"
								].join(" ")}
								onMouseDown={(e) => {
									e.preventDefault();
									handleSelect(suggestion);
								}}
								onMouseEnter={() => setSelectedIndex(index)}
							>
								{trigger === "@" ? (
									<UserCircle
										size={24}
										weight="duotone"
										className="shrink-0 text-muted-foreground"
									/>
								) : (
									<Hash
										size={24}
										weight="duotone"
										className="shrink-0 text-muted-foreground"
									/>
								)}
								<div className="min-w-0 flex-1">
									<p className="truncate font-medium text-foreground">
										{suggestion.name}
									</p>
									{suggestion.subtitle && (
										<p className="truncate text-[13px] text-muted-foreground">
											{suggestion.subtitle}
										</p>
									)}
								</div>
							</button>
						))}
					</div>
				)}

				{trigger !== null &&
					isLoading &&
					suggestions.length === 0 &&
					query.length >= 1 && (
						<div className="absolute left-0 right-0 z-10 mt-1 rounded-lg border border-border bg-white px-3 py-3 shadow-lg">
							<p className="text-center text-sm text-muted-foreground">
								Searching...
							</p>
						</div>
					)}
			</div>
		);
	}
);

MentionHashtagInput.displayName = "MentionHashtagInput";

export default MentionHashtagInput;
